/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import { ArrowLongRightIcon, CalendarIcon, CloseIcon, TimeIcon } from '@fourthwall/icons'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '../../common/Button'
import { Icon } from '../../common/Icon'
import { Popper } from '../../common/Popper'
import { useOutsideClick } from '../../hooks/useOutsideClick'
import { Calendar } from '../../partials/Calendar'
import { useMediaQueryContext } from '../../providers/MediaQueryProvider'
import { InputUI } from '../../ui'
import { Input } from '../Input'
import * as S from './DatePicker.styled'
import { CalendarFooter } from './DatePicker.styled'
import type { Props } from './types'
import { getInternalValue } from './utils'

export const DatePicker = ({
  type = 'single',
  name = [],
  value = [],
  labels,
  timeLabels,
  minDate,
  maxDate,
  suggestion,
  error,
  submitCount,
  size,
  dateFormat,
  showTimeInputs = false,
  initialTimeValues,
  placement = 'bottom',
  strategy,
  disabled = false,
  onChange,
  onClear,
}: Props) => {
  const media = useMediaQueryContext()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputIcon = showTimeInputs ? TimeIcon : CalendarIcon
  const format = dateFormat || (showTimeInputs ? 'D MMM, h:mm A' : 'D MMM')
  const [internalValue, setInternalValue] = useState(getInternalValue(value))
  const showClearButton = !!onClear && value.filter(Boolean).length > 0

  useEffect(() => {
    setInternalValue(getInternalValue(value))
  }, [value])

  useOutsideClick(containerRef, () => {
    setIsOpen(false)
  })

  const handleDatesChange = (dates: Date[]) => {
    setInternalValue(dates)

    if (type === 'range' && dates.length < 2) return

    // NOTE: if timepicker is visible, we handle change by clicking an Apply button
    if (showTimeInputs) return

    onChange?.(dates)
  }

  const handleApplyCLick = () => {
    if (type === 'range' && internalValue.length < 2) return

    onChange?.(internalValue)

    setIsOpen(false)
  }

  const internalLabels = useMemo(() => {
    if (labels) {
      return labels
    }

    if (type === 'single') {
      return ['Date']
    }

    // type === 'range'
    return ['Starts', 'Ends']
  }, [labels, type])

  const getInputValue = (date?: Date) => {
    if (!date) return ''

    return dayjs(date).format(format)
  }

  const renderInputs = () => {
    if (type === 'range') {
      return (
        <>
          <Input
            name={name[0]}
            label={internalLabels?.[0] || ''}
            value={getInputValue(value[0])}
            leftIcon={inputIcon}
            size={size}
            disabled={disabled}
            transparent
            readOnly
          />
          <Icon component={ArrowLongRightIcon} height={16} />
          <Input
            name={name[1]}
            label={internalLabels?.[1] || ''}
            value={getInputValue(value[1])}
            leftIcon={inputIcon}
            size={size}
            disabled={disabled}
            transparent
            readOnly
          />
        </>
      )
    }

    return (
      <Input
        name={name[0]}
        label={internalLabels?.[0] || ''}
        value={getInputValue(value[0])}
        leftIcon={inputIcon}
        size={size}
        disabled={disabled}
        fullWidth
        transparent
      />
    )
  }

  const renderFooter = ({ areSelectedTimesValid }: { areSelectedTimesValid: boolean }) => {
    if (!showTimeInputs) return null

    const onApplyCLick = () => {
      if (!areSelectedTimesValid) return

      handleApplyCLick()
    }

    return (
      <CalendarFooter>
        <Button
          label="Cancel"
          size="small"
          appearance="secondary"
          fullWidth={!media.tablet}
          onClick={() => setIsOpen(false)}
        />
        <Button
          label="Apply"
          size="small"
          appearance="primary"
          fullWidth={!media.tablet}
          onClick={onApplyCLick}
        />
      </CalendarFooter>
    )
  }

  return (
    <Popper
      isOpen={isOpen}
      referenceComponent={
        <S.InputsWrapper>
          <div
            data-testid="DatePicker.Container"
            onClick={!disabled ? () => setIsOpen((isOpen) => !isOpen) : undefined}
          >
            <InputUI.Box
              $size={size}
              $invalid={!!error}
              $focused={isOpen}
              $disabled={disabled}
              $fullWidth
            >
              <S.InputsContainer data-testid="DatePicker.Inputs" $withRightIcon={showClearButton}>
                {renderInputs()}
              </S.InputsContainer>
            </InputUI.Box>
            {!error && suggestion && <InputUI.Suggestion>{suggestion}</InputUI.Suggestion>}
            {!!error && <InputUI.Error errorTimestampKey={submitCount}>{error}</InputUI.Error>}
          </div>
          {showClearButton && (
            <S.CloseIconContainer onClick={onClear}>
              <Icon component={CloseIcon} height={16} />
            </S.CloseIconContainer>
          )}
        </S.InputsWrapper>
      }
      popperProps={{
        placement,
        strategy,
      }}
      style={media.tablet ? { paddingTop: 8, paddingBottom: 8 } : undefined}
      onClose={() => setIsOpen(false)}
    >
      <S.CalendarContainer>
        <Calendar
          type={type}
          showTimeInputs={showTimeInputs}
          selectedDates={internalValue}
          onDatesChange={handleDatesChange}
          timeLabels={timeLabels}
          initialTimeValues={initialTimeValues}
          minDate={minDate}
          maxDate={maxDate}
          footer={renderFooter}
        />
      </S.CalendarContainer>
    </Popper>
  )
}
